import jsPDF from 'jspdf';

// Helper to strip HTML for plain text (used for tests)
const stripHTML = (html) => {
  if (!html) return '';
  const temp = document.createElement('div');
  temp.innerHTML = html;
  return temp.textContent || temp.innerText || '';
};

// Helper to parse HTML and extract formatted segments
const parseFormatting = (html) => {
  if (!html) return [];
  
  const temp = document.createElement('div');
  temp.innerHTML = html;
  
  const segments = [];
  
  const extractText = (node, parentBold = false, parentItalic = false, parentUnderline = false) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent;
      if (text) {
        segments.push({
          text: text,
          bold: parentBold,
          italic: parentItalic,
          underline: parentUnderline
        });
      }
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const tag = node.tagName.toLowerCase();
      const isBold = parentBold || tag === 'b' || tag === 'strong';
      const isItalic = parentItalic || tag === 'i' || tag === 'em';
      const isUnderline = parentUnderline || tag === 'u';
      
      node.childNodes.forEach(child => {
        extractText(child, isBold, isItalic, isUnderline);
      });
    }
  };
  
  temp.childNodes.forEach(node => extractText(node));
  
  return segments.length > 0 ? segments : [{ text: temp.textContent, bold: false, italic: false, underline: false }];
};

// Helper to draw formatted text with word wrapping
const drawFormattedTextWrapped = (pdf, segments, x, y, maxWidth, fontSize = 12, align = 'center', verticalAlign = 'top', containerHeight = null) => {
  pdf.setFontSize(fontSize);
  const lineHeight = fontSize * 0.4;
  
  const lines = [];
  let currentLine = [];
  let currentWidth = 0;
  
  // Word wrap while preserving formatting
  segments.forEach(segment => {
    const words = segment.text.split(/(\s+)/); // Keep spaces
    
    words.forEach(word => {
      if (!word) return;
      
      // Set font for measurement
      const fontStyle = segment.bold && segment.italic ? 'bolditalic' : 
                       segment.bold ? 'bold' : 
                       segment.italic ? 'italic' : 'normal';
      pdf.setFont('times', fontStyle);
      
      const wordWidth = pdf.getTextWidth(word);
      
      // Check if word fits on current line
      if (currentWidth + wordWidth > maxWidth && currentLine.length > 0) {
        lines.push([...currentLine]);
        currentLine = [];
        currentWidth = 0;
      }
      
      currentLine.push({ ...segment, text: word });
      currentWidth += wordWidth;
    });
  });
  
  if (currentLine.length > 0) {
    lines.push(currentLine);
  }
  
  // Calculate total height
  const totalHeight = lines.length * lineHeight;
  
  // Apply vertical alignment
  let currentY = y;
  if (verticalAlign === 'middle' && containerHeight) {
    currentY = y + (containerHeight - totalHeight) / 2;
  } else if (verticalAlign === 'bottom' && containerHeight) {
    currentY = y + containerHeight - totalHeight;
  }
  
  // Draw each line
  lines.forEach(line => {
    let currentX = x;
    
    // Calculate line width for alignment
    let lineWidth = 0;
    line.forEach(seg => {
      const fontStyle = seg.bold && seg.italic ? 'bolditalic' : 
                       seg.bold ? 'bold' : 
                       seg.italic ? 'italic' : 'normal';
      pdf.setFont('times', fontStyle);
      lineWidth += pdf.getTextWidth(seg.text);
    });
    
    // Apply horizontal alignment
    if (align === 'center') {
      currentX = x + (maxWidth - lineWidth) / 2;
    } else if (align === 'right') {
      currentX = x + maxWidth - lineWidth;
    }
    // 'left' uses x as-is
    
    // Draw each segment
    line.forEach(seg => {
      const fontStyle = seg.bold && seg.italic ? 'bolditalic' : 
                       seg.bold ? 'bold' : 
                       seg.italic ? 'italic' : 'normal';
      pdf.setFont('times', fontStyle);
      
      pdf.text(seg.text, currentX, currentY);
      
      // Draw underline if needed
      if (seg.underline) {
        const width = pdf.getTextWidth(seg.text);
        pdf.line(currentX, currentY + 0.5, currentX + width, currentY + 0.5);
      }
      
      currentX += pdf.getTextWidth(seg.text);
    });
    
    currentY += lineHeight;
  });
  
  return currentY;
};

/**
 * Generate printable flashcards for cutting out
 * @param {Array} cards - Array of flashcard objects
 * @param {string} setTitle - Title of the flashcard set
 */
export const generatePrintableFlashcards = async (cards, setTitle) => {
  const pdf = new jsPDF('l', 'mm', 'a4'); // LANDSCAPE orientation
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  
  // Card dimensions (4 cards per page, 2x2 grid) - landscape cards
  const cardWidth = (pageWidth - 30) / 2;
  const cardHeight = (pageHeight - 30) / 2;
  const margin = 10;
  const padding = 8;
  
  // Title page with logo
  let yPosition = 30;
  
  try {
    const logoImg = await loadImage('/logo.png');
    pdf.addImage(logoImg, 'PNG', pageWidth / 2 - 12.5, yPosition, 25, 25);
    yPosition += 35;
  } catch (error) {
    console.error('Failed to load logo:', error);
    yPosition += 5;
  }
  
  pdf.setFontSize(24);
  pdf.setFont('times', 'bold');
  pdf.text(setTitle, pageWidth / 2, yPosition, { align: 'center' });
  yPosition += 10;
  
  pdf.setFontSize(12);
  pdf.setFont('times', 'normal');
  pdf.text('Printable Flashcards - Cut along the dotted lines', pageWidth / 2, yPosition, { align: 'center' });
  yPosition += 8;
  pdf.text(`${cards.length} cards - Print double-sided, flip on short edge`, pageWidth / 2, yPosition, { align: 'center' });
  
  // Add BLANK PAGE after title to prevent cards from printing on backside of title
  pdf.addPage();
  
  // Process cards in sets of 4 (one page at a time)
  for (let pageStart = 0; pageStart < cards.length; pageStart += 4) {
    // Add FRONT page (questions)
    pdf.addPage();
    
    for (let i = 0; i < 4 && (pageStart + i) < cards.length; i++) {
      const card = cards[pageStart + i];
      const col = i % 2;
      const row = Math.floor(i / 2);
      const x = margin + (col * (cardWidth + margin));
      const y = margin + (row * (cardHeight + margin));
      
      // Draw card border (dashed)
      pdf.setLineDash([2, 2]);
      pdf.rect(x, y, cardWidth, cardHeight);
      pdf.setLineDash([]);
      
      // Draw FRONT side (Question) - CENTERED with formatting
      const questionSegments = parseFormatting(card.question);
      drawFormattedTextWrapped(pdf, questionSegments, x + padding, y + padding, cardWidth - 2 * padding, 18, 'center', 'middle', cardHeight - 2 * padding);
      
      // Add card number at bottom
      pdf.setFontSize(7);
      pdf.text(`#${pageStart + i + 1}`, x + cardWidth - padding - 10, y + cardHeight - padding);
    }
    
    // Add BACK page (answers) - MIRRORED for double-sided printing
    pdf.addPage();
    
    for (let i = 0; i < 4 && (pageStart + i) < cards.length; i++) {
      const card = cards[pageStart + i];
      
      // Mirror positions: [0,1,2,3] becomes [1,0,3,2] for correct double-sided alignment
      const mirrorMap = [1, 0, 3, 2];
      const mirroredPos = mirrorMap[i];
      const col = mirroredPos % 2;
      const row = Math.floor(mirroredPos / 2);
      const x = margin + (col * (cardWidth + margin));
      const y = margin + (row * (cardHeight + margin));
      
      // Draw card border (dashed)
      pdf.setLineDash([2, 2]);
      pdf.rect(x, y, cardWidth, cardHeight);
      pdf.setLineDash([]);
      
      // Draw BACK side (Answer) - CENTERED with formatting
      const answerSegments = parseFormatting(card.answer);
      drawFormattedTextWrapped(pdf, answerSegments, x + padding, y + padding, cardWidth - 2 * padding, 18, 'center', 'middle', cardHeight - 2 * padding);
      
      // Add card number at bottom
      pdf.setFontSize(7);
      pdf.text(`#${pageStart + i + 1}`, x + cardWidth - padding - 10, y + cardHeight - padding);
    }
  }
  
  // Save PDF
  pdf.save(`${setTitle.replace(/[^a-z0-9]/gi, '_')}_flashcards.pdf`);
};

/**
 * Generate test format with randomized questions
 * @param {Array} cards - Array of flashcard objects
 * @param {string} setTitle - Title of the flashcard set
 */
export const generateFlashcardTest = async (cards, setTitle) => {
  const pdf = new jsPDF('p', 'mm', 'a4');
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 20;
  let yPosition = margin;
  
  // Shuffle cards
  const shuffledCards = [...cards].sort(() => Math.random() - 0.5);
  
  // Load and add logo
  try {
    const logoImg = await loadImage('/logo.png');
    pdf.addImage(logoImg, 'PNG', pageWidth / 2 - 12.5, yPosition, 25, 25);
    yPosition += 35; // Increased spacing from 30 to 35
  } catch (error) {
    console.error('Failed to load logo:', error);
    yPosition += 5;
  }
  
  // Header
  pdf.setFontSize(20);
  pdf.setFont('times', 'bold');
  pdf.text(setTitle, pageWidth / 2, yPosition, { align: 'center' });
  yPosition += 8;
  
  pdf.setFontSize(12);
  pdf.setFont('times', 'italic');
  pdf.text('Practice Test', pageWidth / 2, yPosition, { align: 'center' });
  yPosition += 15; // More space after header
  
  // Name, Date, Score fields with more spacing
  pdf.setFontSize(10);
  pdf.setFont('times', 'normal');
  pdf.text('Name: ___________________________________', margin, yPosition);
  yPosition += 8;
  pdf.text('Date: ___________________________________', margin, yPosition);
  yPosition += 8;
  pdf.text('Score: _________ / ' + shuffledCards.length, margin, yPosition);
  yPosition += 15;
  
  // Questions with single answer line
  shuffledCards.forEach((card, index) => {
    // Check if we need a new page (need ~15mm for question + line)
    if (yPosition > pageHeight - 25) {
      pdf.addPage();
      yPosition = margin;
    }
    
    // Question number and text with formatting
    pdf.setFontSize(11);
    
    // Add question number as a segment
    const questionSegments = parseFormatting(card.question);
    const numberedSegments = [
      { text: `${index + 1}. `, bold: true, italic: false, underline: false },
      ...questionSegments
    ];
    
    // Draw formatted question text (left-aligned)
    const endY = drawFormattedTextWrapped(pdf, numberedSegments, margin, yPosition, pageWidth - 2 * margin, 11, 'left');
    yPosition = endY;
    
    // Single answer line starting right after question
    pdf.setLineDash([1, 1]);
    pdf.line(margin, yPosition + 2, pageWidth - margin, yPosition + 2);
    pdf.setLineDash([]);
    
    yPosition += 10; // Space before next question
  });
  
  // Answer key in two columns on separate pages
  pdf.addPage();
  yPosition = margin;
  
  pdf.setFontSize(16);
  pdf.setFont('times', 'bold');
  pdf.text('ANSWER KEY', pageWidth / 2, yPosition, { align: 'center' });
  yPosition += 12;
  
  // Two column layout - fill left column completely first
  const columnWidth = (pageWidth - 3 * margin) / 2;
  const leftColumnX = margin;
  const rightColumnX = pageWidth / 2 + margin / 2;
  const maxY = pageHeight - 20; // Bottom margin
  
  let currentX = leftColumnX;
  let currentY = yPosition;
  let isLeftColumn = true;
  
  shuffledCards.forEach((card, index) => {
    // Parse answer formatting
    pdf.setFontSize(9);
    const answerSegments = parseFormatting(card.answer);
    
    // Add answer number as a segment
    const numberedAnswerSegments = [
      { text: `${index + 1}. `, bold: true, italic: false, underline: false },
      ...answerSegments
    ];
    
    // Estimate answer height (rough calculation)
    const answerText = stripHTML(card.answer);
    const answerLines = pdf.splitTextToSize(`${index + 1}. ${answerText}`, columnWidth - 8);
    const answerHeight = Math.max(answerLines.length * 4, 6) + 2;
    
    // Check if answer fits in current column
    if (currentY + answerHeight > maxY) {
      if (isLeftColumn) {
        // Move to right column
        isLeftColumn = false;
        currentX = rightColumnX;
        currentY = yPosition;
      } else {
        // Add new page
        pdf.addPage();
        currentY = margin;
        currentX = leftColumnX;
        isLeftColumn = true;
      }
    }
    
    // Draw formatted answer text (left-aligned)
    const endY = drawFormattedTextWrapped(pdf, numberedAnswerSegments, currentX, currentY, columnWidth - 8, 9, 'left');
    currentY = endY + 2; // Add small spacing after answer
  });
  
  // Save PDF
  pdf.save(`${setTitle.replace(/[^a-z0-9]/gi, '_')}_test.pdf`);
};

/**
 * Generate PDF for a single lesson
 * @param {Object} lesson - The lesson data
 * @param {Object} session - The session data
 * @param {string} userName - User's name for personalization
 */
export const generateLessonPDF = async (lesson, session, userName) => {
  const pdf = new jsPDF('p', 'mm', 'a4');
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 15;
  let yPosition = margin;

  // Load footer logo
  let footerLogoImg = null;
  try {
    footerLogoImg = await loadImage('/footer_logo.png');
  } catch (error) {
    console.error('Failed to load footer logo:', error);
  }

  // Load and add title page logo
  try {
    const logoImg = await loadImage('/logo.png');
    pdf.addImage(logoImg, 'PNG', margin, yPosition, 25, 25);
    yPosition += 30;
  } catch (error) {
    console.error('Failed to load logo:', error);
    yPosition += 5;
  }

  // Title - Bible Thumper: Teen Edition
  pdf.setFontSize(24);
  pdf.setFont('times', 'bold');
  pdf.text('Bible Thumper: Teen Edition', pageWidth / 2, yPosition, { align: 'center' });
  yPosition += 10;

  // Session and Lesson Info
  pdf.setFontSize(16);
  pdf.setFont('times', 'normal');
  // Split long session titles to multiple lines
  const sessionTitleLines = pdf.splitTextToSize(session.title, pageWidth - 2 * margin);
  sessionTitleLines.forEach(line => {
    pdf.text(line, pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 6;
  });
  yPosition += 2;
  
  pdf.setFontSize(14);
  pdf.setFont('times', 'bold');
  // Split long lesson titles to multiple lines
  const lessonTitleLines = pdf.splitTextToSize(lesson.title, pageWidth - 2 * margin);
  lessonTitleLines.forEach(line => {
    pdf.text(line, pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 7; // Line spacing for title
  });
  yPosition += 2; // Extra space after title
  
  // Add scripture reference subtitle
  pdf.setFontSize(12);
  pdf.setFont('times', 'italic');
  pdf.text(lesson.reference || '', pageWidth / 2, yPosition, { align: 'center' });
  yPosition += 8;
  
  pdf.setFontSize(10);
  pdf.setFont('times', 'normal');
  pdf.text(`Prepared for: ${userName}`, pageWidth / 2, yPosition, { align: 'center' });
  yPosition += 12;

  // Add line separator
  pdf.setLineWidth(0.5);
  pdf.line(margin, yPosition, pageWidth - margin, yPosition);
  yPosition += 8;

  // PRAYER TIME
  yPosition = addSection(pdf, 'Prayer Time', yPosition, margin, pageWidth, pageHeight);
  pdf.setFontSize(12);
  pdf.setFont('times', 'normal');
  lesson.prayer_checklist?.forEach((prayer) => {
    yPosition = checkPageBreak(pdf, yPosition, pageHeight, margin);
    // Draw checkbox square
    pdf.rect(margin + 5, yPosition - 3, 4, 4);
    pdf.text(prayer, margin + 12, yPosition);
    yPosition += 6;
  });
  yPosition += 5;

  // MEDITATE
  yPosition = addSection(pdf, 'Meditate', yPosition, margin, pageWidth, pageHeight);
  yPosition = checkPageBreak(pdf, yPosition, pageHeight, margin);
  pdf.setFontSize(12);
  
  // Add scripture reference before the text
  pdf.setFont('times', 'bold');
  pdf.text(lesson.reference || '', margin + 5, yPosition);
  yPosition += 7;
  pdf.setFont('times', 'normal');
  
  const scriptureLines = pdf.splitTextToSize(lesson.scripture || '', pageWidth - 2 * margin);
  scriptureLines.forEach(line => {
    yPosition = checkPageBreak(pdf, yPosition, pageHeight, margin);
    pdf.text(line, margin + 5, yPosition);
    yPosition += 6;
  });
  yPosition += 8;

  // KEY WORDS - Prompt for user to select (without suggested list)
  if (lesson.key_words && lesson.key_words.length > 0) {
    yPosition = addSection(pdf, 'Key Words', yPosition, margin, pageWidth, pageHeight);
    yPosition = checkPageBreak(pdf, yPosition, pageHeight, margin);
    pdf.setFontSize(12);
    pdf.setFont('times', 'italic');
    pdf.text('Select key words from the passage:', margin + 5, yPosition);
    yPosition += 10;
    // Add space for user to write their own key words
    pdf.setFont('times', 'normal');
    pdf.text('_________________________________________________________________', margin + 5, yPosition);
    yPosition += 8;
    pdf.text('_________________________________________________________________', margin + 5, yPosition);
    yPosition += 10;
  }

  // MEMORIZATION
  if (lesson.memorization && lesson.memorization.length > 0) {
    yPosition = addSection(pdf, 'Memorization', yPosition, margin, pageWidth, pageHeight);
    pdf.setFontSize(12);
    pdf.setFont('times', 'normal');
    lesson.memorization.forEach((memo) => {
      yPosition = checkPageBreak(pdf, yPosition, pageHeight, margin);
      const verseNum = memo.verse_number || '';
      const mnemonic = memo.mnemonic || '';
      pdf.text(`${verseNum}: ${mnemonic}`, margin + 5, yPosition);
      yPosition += 6;
    });
    yPosition += 8;
  }

  // ACTIVITIES (without answers) - with 1.5 spacing
  if (lesson.activities && lesson.activities.length > 0) {
    yPosition = addSection(pdf, 'Activities', yPosition, margin, pageWidth, pageHeight);
    pdf.setFontSize(12);
    pdf.setFont('times', 'normal');
    lesson.activities.forEach((activity) => {
      yPosition = checkPageBreak(pdf, yPosition, pageHeight, margin);
      const verseRef = activity.verse_number || '';
      const activityText = activity.activity_text || activity.text || '';
      const answers = activity.answers || [];
      
      // Replace each blank with appropriate length based on the actual word
      let textWithProportionalBlanks = activityText;
      let answerIndex = 0;
      textWithProportionalBlanks = activityText.replace(/____+/g, (match) => {
        if (answerIndex < answers.length) {
          const word = answers[answerIndex];
          answerIndex++;
          // Formula: 2.3 characters per letter, rounded up
          const blankLength = Math.ceil(word.length * 2.3);
          return '_'.repeat(blankLength);
        }
        return '_'.repeat(20); // Fallback if no answer available
      });
      
      const activityLines = pdf.splitTextToSize(`${verseRef}: ${textWithProportionalBlanks}`, pageWidth - 2 * margin - 10);
      activityLines.forEach(line => {
        yPosition = checkPageBreak(pdf, yPosition, pageHeight, margin);
        pdf.text(line, margin + 5, yPosition);
        yPosition += 9; // 1.5 spacing (6 * 1.5 = 9)
      });
      yPosition += 6; // Extra space between activities
    });
    yPosition += 5;
  }

  // TRIVIA (without answers) - adequate space for answers
  if (lesson.trivia && lesson.trivia.length > 0) {
    yPosition = addSection(pdf, 'Trivia Questions', yPosition, margin, pageWidth, pageHeight);
    pdf.setFontSize(12);
    pdf.setFont('times', 'normal');
    lesson.trivia.forEach((question, index) => {
      yPosition = checkPageBreak(pdf, yPosition, pageHeight, margin);
      const questionLines = pdf.splitTextToSize(
        `${index + 1}. ${question.question}`,
        pageWidth - 2 * margin - 10
      );
      // Print question lines with normal spacing
      questionLines.forEach((line, lineIndex) => {
        yPosition = checkPageBreak(pdf, yPosition, pageHeight, margin);
        pdf.text(line, margin + 5, yPosition);
        yPosition += 6; // Normal line spacing for question text
      });
      // Add extra space after the question for writing the answer
      yPosition += 12; // Space for answer
    });
    yPosition += 5;
  }

  // CLOSING PRAYER
  yPosition = addSection(pdf, 'Closing Prayer', yPosition, margin, pageWidth, pageHeight);
  pdf.setFontSize(12);
  pdf.setFont('times', 'normal');
  lesson.closing_prayer?.forEach((prayer) => {
    yPosition = checkPageBreak(pdf, yPosition, pageHeight, margin);
    // Draw checkbox square
    pdf.rect(margin + 5, yPosition - 3, 4, 4);
    pdf.text(prayer, margin + 12, yPosition);
    yPosition += 6;
  });

  // Add footers to all pages (lessons don't have a separate title page)
  const totalPages = pdf.internal.getNumberOfPages();
  const footerText = `Bible Thumper: Teen Edition, ${lesson.title}: ${lesson.reference || ''}`;
  
  for (let i = 1; i <= totalPages; i++) {
    pdf.setPage(i);
    await addFooter(pdf, i, totalPages, footerText, footerLogoImg);
  }

  // Save PDF
  pdf.save(`${lesson.lesson_id}_${userName.replace(/\s+/g, '_')}.pdf`);
};

/**
 * Generate PDF for entire session
 */
export const generateSessionPDF = async (lessons, session, userName) => {
  const pdf = new jsPDF('p', 'mm', 'a4');
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 15;
  let yPosition = margin;

  // Load footer logo
  let footerLogoImg = null;
  try {
    footerLogoImg = await loadImage('/footer_logo.png');
  } catch (error) {
    console.error('Failed to load footer logo:', error);
  }

  // Cover Page
  try {
    const logoImg = await loadImage('/logo.png');
    pdf.addImage(logoImg, 'PNG', pageWidth / 2 - 30, 60, 60, 60);
  } catch (error) {
    console.error('Failed to load logo:', error);
  }

  pdf.setFontSize(28);
  pdf.setFont('times', 'bold');
  pdf.text('Bible Thumper: Teen Edition', pageWidth / 2, 140, { align: 'center' });
  
  pdf.setFontSize(20);
  pdf.setFont('times', 'normal');
  // Split long session titles to multiple lines on cover page
  let coverYPos = 155;
  const coverSessionLines = pdf.splitTextToSize(session.title, pageWidth - 2 * margin);
  coverSessionLines.forEach(line => {
    pdf.text(line, pageWidth / 2, coverYPos, { align: 'center' });
    coverYPos += 8;
  });
  
  pdf.setFontSize(14);
  pdf.text(`Study Guide for: ${userName}`, pageWidth / 2, coverYPos + 10, { align: 'center' });
  
  pdf.setFontSize(12);
  pdf.text(`${lessons.length} Lessons`, pageWidth / 2, coverYPos + 25, { align: 'center' });

  // Generate each lesson
  for (let i = 0; i < lessons.length; i++) {
    pdf.addPage();
    await addLessonToExistingPDF(pdf, lessons[i], session, userName, i + 1);
  }

  // Add footers to all pages except the first (cover page)
  const totalPages = pdf.internal.getNumberOfPages();
  const footerText = `Bible Thumper: Teen Edition, ${session.title}`;
  
  for (let i = 2; i <= totalPages; i++) {
    pdf.setPage(i);
    await addFooter(pdf, i - 1, totalPages - 1, footerText, footerLogoImg);
  }

  // Save PDF
  pdf.save(`${session.session_id}_Complete_${userName.replace(/\s+/g, '_')}.pdf`);
};

// Helper functions
const loadImage = (src) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
};

const addSection = (pdf, title, yPosition, margin, pageWidth, pageHeight, minSpaceForContent = 20) => {
  // Smart break: ensure section title has at least minSpaceForContent below it
  // If not enough space, move to new page
  if (yPosition + 10 + 8 + minSpaceForContent > pageHeight - 30) {
    pdf.addPage();
    yPosition = margin;
  } else {
    yPosition += 10;
  }
  
  pdf.setFontSize(18);
  pdf.setFont('times', 'bold');
  pdf.text(title, margin, yPosition);
  return yPosition + 8;
};

const checkPageBreak = (pdf, yPosition, pageHeight, margin) => {
  if (yPosition > pageHeight - 30) {
    pdf.addPage();
    return margin;
  }
  return yPosition;
};

// Add footer to page with logo, text, and page numbers
const addFooter = async (pdf, pageNum, totalPages, footerText, footerLogoImg) => {
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const footerY = pageHeight - 10; // 10mm from bottom
  const footerMargin = 15;
  const logoWidth = 8;
  const textStartX = footerMargin + logoWidth + 2; // 2mm gap after logo
  
  // Calculate available width for footer text (leave space for page numbers)
  const pageNumText = `page ${pageNum} of ${totalPages}`;
  pdf.setFontSize(8);
  pdf.setFont('times', 'normal');
  const pageNumWidth = pdf.getTextWidth(pageNumText);
  const availableTextWidth = pageWidth - textStartX - pageNumWidth - footerMargin - 5; // 5mm gap before page num
  
  // Add footer logo (small sketch)
  if (footerLogoImg) {
    try {
      pdf.addImage(footerLogoImg, 'PNG', footerMargin, footerY - 5, logoWidth, 8);
    } catch (error) {
      console.error('Failed to add footer logo:', error);
    }
  }
  
  // Split footer text if too long
  const footerLines = pdf.splitTextToSize(footerText, availableTextWidth);
  
  // If text wraps to multiple lines, adjust position
  if (footerLines.length > 1) {
    // Start higher to accommodate multiple lines
    let textY = footerY - (footerLines.length - 1) * 3; // 3mm per extra line
    footerLines.forEach((line, index) => {
      pdf.text(line, textStartX, textY + (index * 3));
    });
  } else {
    // Single line - normal position
    pdf.text(footerText, textStartX, footerY);
  }
  
  // Add page number (right-aligned)
  pdf.text(pageNumText, pageWidth - footerMargin, footerY, { align: 'right' });
};

const addLessonToExistingPDF = async (pdf, lesson, session, userName, lessonNumber) => {
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 15;
  let yPosition = margin;

  // Lesson header - wrap if long
  pdf.setFontSize(20);
  pdf.setFont('times', 'bold');
  const lessonHeaderLines = pdf.splitTextToSize(`Lesson ${lessonNumber}: ${lesson.title}`, pageWidth - 2 * margin);
  lessonHeaderLines.forEach(line => {
    pdf.text(line, pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 8; // Line spacing for header
  });
  yPosition += 2; // Extra space after header
  
  // Add scripture reference subtitle
  pdf.setFontSize(12);
  pdf.setFont('times', 'italic');
  pdf.text(lesson.reference || '', pageWidth / 2, yPosition, { align: 'center' });
  yPosition += 10;

  pdf.setLineWidth(0.5);
  pdf.line(margin, yPosition, pageWidth - margin, yPosition);
  yPosition += 8;

  // Add all content (similar to single lesson PDF)
  yPosition = addSection(pdf, 'Prayer Time', yPosition, margin, pageWidth, pageHeight);
  pdf.setFontSize(12);
  pdf.setFont('times', 'normal');
  lesson.prayer_checklist?.forEach((prayer) => {
    yPosition = checkPageBreak(pdf, yPosition, pageHeight, margin);
    // Draw checkbox square
    pdf.rect(margin + 5, yPosition - 3, 4, 4);
    pdf.text(prayer, margin + 12, yPosition);
    yPosition += 6;
  });
  yPosition += 5;

  yPosition = addSection(pdf, 'Meditate', yPosition, margin, pageWidth, pageHeight);
  pdf.setFontSize(12);
  
  // Add scripture reference before the text
  pdf.setFont('times', 'bold');
  pdf.text(lesson.reference || '', margin + 5, yPosition);
  yPosition += 7;
  pdf.setFont('times', 'normal');
  
  const scriptureLines = pdf.splitTextToSize(lesson.scripture || '', pageWidth - 2 * margin);
  scriptureLines.forEach(line => {
    yPosition = checkPageBreak(pdf, yPosition, pageHeight, margin);
    pdf.text(line, margin + 5, yPosition);
    yPosition += 6;
  });
  yPosition += 8;

  // KEY WORDS - Prompt for user to select (without suggested list)
  if (lesson.key_words && lesson.key_words.length > 0) {
    yPosition = addSection(pdf, 'Key Words', yPosition, margin, pageWidth, pageHeight);
    yPosition = checkPageBreak(pdf, yPosition, pageHeight, margin);
    pdf.setFontSize(12);
    pdf.setFont('times', 'italic');
    pdf.text('Select key words from the passage:', margin + 5, yPosition);
    yPosition += 10;
    // Add space for user to write their own key words
    pdf.setFont('times', 'normal');
    pdf.text('_________________________________________________________________', margin + 5, yPosition);
    yPosition += 8;
    pdf.text('_________________________________________________________________', margin + 5, yPosition);
    yPosition += 10;
  }

  if (lesson.memorization && lesson.memorization.length > 0) {
    yPosition = addSection(pdf, 'Memorization', yPosition, margin, pageWidth, pageHeight);
    pdf.setFontSize(12);
    pdf.setFont('times', 'normal');
    lesson.memorization.forEach((memo) => {
      yPosition = checkPageBreak(pdf, yPosition, pageHeight, margin);
      const verseNum = memo.verse_number || '';
      const mnemonic = memo.mnemonic || '';
      pdf.text(`${verseNum}: ${mnemonic}`, margin + 5, yPosition);
      yPosition += 6;
    });
    yPosition += 8;
  }

  if (lesson.activities && lesson.activities.length > 0) {
    yPosition = addSection(pdf, 'Activities', yPosition, margin, pageWidth, pageHeight);
    pdf.setFontSize(12);
    pdf.setFont('times', 'normal');
    lesson.activities.forEach((activity) => {
      yPosition = checkPageBreak(pdf, yPosition, pageHeight, margin);
      const verseRef = activity.verse_number || '';
      const activityText = activity.activity_text || activity.text || '';
      const answers = activity.answers || [];
      
      // Replace each blank with appropriate length based on the actual word
      let textWithProportionalBlanks = activityText;
      let answerIndex = 0;
      textWithProportionalBlanks = activityText.replace(/____+/g, (match) => {
        if (answerIndex < answers.length) {
          const word = answers[answerIndex];
          answerIndex++;
          // Formula: 2.3 characters per letter, rounded up
          const blankLength = Math.ceil(word.length * 2.3);
          return '_'.repeat(blankLength);
        }
        return '_'.repeat(20); // Fallback if no answer available
      });
      
      const lines = pdf.splitTextToSize(`${verseRef}: ${textWithProportionalBlanks}`, pageWidth - 2 * margin - 10);
      lines.forEach(line => {
        yPosition = checkPageBreak(pdf, yPosition, pageHeight, margin);
        pdf.text(line, margin + 5, yPosition);
        yPosition += 9; // 1.5 spacing (6 * 1.5 = 9)
      });
      yPosition += 6; // Extra space between activities
    });
    yPosition += 5;
  }

  if (lesson.trivia && lesson.trivia.length > 0) {
    yPosition = addSection(pdf, 'Trivia Questions', yPosition, margin, pageWidth, pageHeight);
    pdf.setFontSize(12);
    pdf.setFont('times', 'normal');
    lesson.trivia.forEach((question, index) => {
      yPosition = checkPageBreak(pdf, yPosition, pageHeight, margin);
      const questionLines = pdf.splitTextToSize(
        `${index + 1}. ${question.question}`,
        pageWidth - 2 * margin - 10
      );
      // Print question lines with normal spacing
      questionLines.forEach((line, lineIndex) => {
        yPosition = checkPageBreak(pdf, yPosition, pageHeight, margin);
        pdf.text(line, margin + 5, yPosition);
        yPosition += 6; // Normal line spacing for question text
      });
      // Add extra space after the question for writing the answer
      yPosition += 12; // Space for answer
    });
    yPosition += 5;
  }

  yPosition = addSection(pdf, 'Closing Prayer', yPosition, margin, pageWidth, pageHeight);
  pdf.setFontSize(12);
  pdf.setFont('times', 'normal');
  lesson.closing_prayer?.forEach((prayer) => {
    yPosition = checkPageBreak(pdf, yPosition, pageHeight, margin);
    // Draw checkbox square
    pdf.rect(margin + 5, yPosition - 3, 4, 4);
    pdf.text(prayer, margin + 12, yPosition);
    yPosition += 6;
  });
};
